import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

const TYPE_COLOR = {
  "Contrato": "bg-primary/10 text-primary",
  "Contratista": "bg-blue-100 text-blue-700",
  "Estimación": "bg-amber-100 text-amber-700",
  "Foto": "bg-emerald-100 text-emerald-700",
  "Bitácora": "bg-purple-100 text-purple-700",
  "Orden de Trabajo": "bg-indigo-100 text-indigo-700",
  "Empleado": "bg-teal-100 text-teal-700",
};

export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      setLoading(true);
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`); setResults(data.results || []); setOpen(true); }
      catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (to) => { setOpen(false); setQ(""); setResults([]); navigate(to); };

  return (
    <div className="relative w-full max-w-md" ref={boxRef} data-testid="global-search">
      <div className="flex items-center gap-2 h-9 px-3 rounded-sm border border-input bg-background focus-within:ring-1 focus-within:ring-primary/40">
        <MagnifyingGlass size={16} className="text-muted-foreground shrink-0" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Buscar contratos, contratistas, estimaciones, fotos, bitácora…"
          data-testid="global-search-input"
          className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
        />
        {q && <button onClick={() => { setQ(""); setResults([]); }} className="text-muted-foreground hover:text-foreground"><X size={14} weight="bold" /></button>}
      </div>
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 mt-1 bg-card border border-border rounded-sm shadow-xl z-50 max-h-[70vh] overflow-y-auto" data-testid="global-search-results">
          {loading && <div className="px-3 py-3 text-sm text-muted-foreground">Buscando…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Sin resultados para “{q}”.</div>}
          {!loading && results.map((r, i) => (
            <button key={i} onClick={() => go(r.to)} data-testid={`search-result-${i}`}
              className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-muted transition-colors duration-150 border-b border-border/50 last:border-0">
              <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm shrink-0 ${TYPE_COLOR[r.type] || "bg-muted text-muted-foreground"}`}>{r.type}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium truncate">{r.label}</span>
                {r.sublabel && <span className="block text-xs text-muted-foreground truncate">{r.sublabel}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
